import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const LEVEL_NAMES = [
  "None", "Spark", "Opening", "Resonance", "Safety", "Curiosity", 
  "Attraction", "Vulnerability", "Alignment", "Momentum", 
  "Intimacy", "Integration", "Commitment", "Union"
];

const LEVEL_DETAILS = [
  { level: 0, name: "None", description: "No connection established yet." },
  { level: 1, name: "Spark", description: "First awareness and curiosity, something catches your attention." },
  { level: 2, name: "Opening", description: "Initial contact begins; energy, tone, and intent are revealed." },
  { level: 3, name: "Resonance", description: "Conversation flows and mutual interest starts to feel natural." },
  { level: 4, name: "Safety", description: "Trust forms through consistency, respect, and emotional ease." },
  { level: 5, name: "Curiosity", description: "You begin wanting to know more - values, stories, inner worlds." },
  { level: 6, name: "Attraction", description: "Chemistry builds across emotional, mental, and physical levels." },
  { level: 7, name: "Vulnerability", description: "Walls soften; honesty and real feelings are shared." },
  { level: 8, name: "Alignment", description: "Values, desires, and intentions start to line up." },
  { level: 9, name: "Momentum", description: "Connection strengthens through shared time, effort, and intention." },
  { level: 10, name: "Intimacy", description: "Emotional and/or physical closeness deepens meaningfully." },
  { level: 11, name: "Integration", description: "You begin weaving each other into daily life and routines." },
  { level: 12, name: "Commitment", description: "Clear choice is made to prioritize and invest in the connection." },
  { level: 13, name: "Union", description: "A stable, mutual bond rooted in trust, love, and shared direction." },
];

export const ConnectionLevelModal = ({
  isOpen,
  onClose,
  currentLevel,
  driftInfo,
  levelScrollRef,
  // Escalation mode props
  isEscalationMode,
  escalationState,
  // Match data for non-escalation mode
  matchPersonaConnection,
}) => {
  const driftBonus = driftInfo?.drift_bonus || 0;
  
  // Determine the actual current level based on mode
  const displayLevel = isEscalationMode && escalationState 
    ? escalationState.level 
    : (matchPersonaConnection || currentLevel || 0);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-divine-charcoal border-divine-gold/30 max-w-md max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-divine-gold font-cinzel text-xl text-center">
            Connection Levels
          </DialogTitle>
        </DialogHeader>
        
        {/* Current Level Display - Shows Escalation Level with Drift in escalation mode */}
        {isEscalationMode && escalationState ? (
          <div className="flex flex-col items-center py-4 border-b border-white/10">
            {/* Circle with current escalation level */}
            <div className="relative">
              <svg 
                className={`w-20 h-20 drop-shadow-lg ${
                  driftBonus === 1 ? "animate-pulse" :
                  driftBonus >= 2 ? "animate-[pulse_0.5s_ease-in-out_infinite]" : ""
                }`} 
                viewBox="0 0 48 48"
              >
                <circle 
                  cx="24" cy="24" r="22" 
                  fill="rgba(15,15,20,0.9)" 
                  stroke={
                    driftBonus >= 2 ? "rgba(147,51,234,0.6)" :
                    driftBonus === 1 ? "rgba(34,197,94,0.5)" :
                    "rgba(16,185,129,0.5)"
                  }
                  strokeWidth="3"
                  className="transition-colors duration-500"
                />
                <circle 
                  cx="24" cy="24" r="19" 
                  fill="none" 
                  stroke={
                    driftBonus >= 2 ? "rgba(147,51,234,0.8)" :
                    driftBonus === 1 ? "rgba(34,197,94,0.7)" :
                    "rgba(16,185,129,0.7)"
                  }
                  strokeWidth="2"
                  className="transition-colors duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`font-bold text-2xl transition-colors duration-500 ${
                  driftBonus >= 2 ? "text-purple-400" :
                  driftBonus === 1 ? "text-green-400" :
                  "text-emerald-400"
                }`}>
                  {escalationState.level}
                </span>
              </div>
            </div>
            <h3 className={`font-semibold text-lg mt-3 transition-colors duration-500 ${
              driftBonus >= 2 ? "text-purple-400" :
              driftBonus === 1 ? "text-green-400" :
              "text-emerald-400"
            }`}>
              {escalationState.level_label || LEVEL_NAMES[escalationState.level]}
            </h3>
            
            {/* Boundary Drift Adjustment Display */}
            {driftBonus > 0 && (
              <div className={`mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                driftBonus >= 2 
                  ? "bg-purple-500/20 text-purple-300 animate-[pulse_0.5s_ease-in-out_infinite]" 
                  : "bg-green-500/20 text-green-300 animate-pulse"
              }`}>
                Boundary Drift Active: +{driftBonus} → Adjusted Level {escalationState.level + driftBonus}
              </div>
            )}
            
            <p className="text-white/70 text-sm text-center px-4 mt-1">
              {LEVEL_DETAILS[escalationState.level]?.description}
            </p>
            
            {/* XP Progress */}
            <div className="w-full max-w-[200px] mt-3">
              <div className="flex justify-between text-xs text-white/50 mb-1">
                <span>XP: {Math.round(escalationState.xp)}/100</span>
                <span className={escalationState.trend === "up" ? "text-green-400" : escalationState.trend === "down" ? "text-red-400" : ""}>
                  {escalationState.trend === "up" ? "↑" : escalationState.trend === "down" ? "↓" : "→"}
                </span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    driftBonus >= 2 ? "bg-purple-500" :
                    driftBonus === 1 ? "bg-green-500" :
                    "bg-emerald-500"
                  }`}
                  style={{ width: `${Math.max(0, Math.min(100, escalationState.xp))}%` }}
                />
              </div>
            </div>
          </div>
        ) : matchPersonaConnection ? (
          <div className="flex flex-col items-center py-4 border-b border-white/10">
            {/* Circle with current level - now drift-aware */}
            <div className="relative">
              <svg 
                className={`w-20 h-20 drop-shadow-lg ${
                  driftBonus === 1 ? "animate-pulse" :
                  driftBonus >= 2 ? "animate-[pulse_0.5s_ease-in-out_infinite]" : ""
                }`} 
                viewBox="0 0 48 48"
              >
                <circle 
                  cx="24" cy="24" r="22" 
                  fill="rgba(15,15,20,0.9)" 
                  stroke={
                    driftBonus >= 2 ? "rgba(147,51,234,0.6)" :
                    driftBonus === 1 ? "rgba(34,197,94,0.5)" :
                    "rgba(212,175,55,0.5)"
                  }
                  strokeWidth="3"
                  className="transition-colors duration-500"
                />
                <circle 
                  cx="24" cy="24" r="19" 
                  fill="none" 
                  stroke={
                    driftBonus >= 2 ? "rgba(147,51,234,0.8)" :
                    driftBonus === 1 ? "rgba(34,197,94,0.7)" :
                    "rgba(212,175,55,0.7)"
                  }
                  strokeWidth="2"
                  className="transition-colors duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`font-bold text-2xl transition-colors duration-500 ${
                  driftBonus >= 2 ? "text-purple-400" :
                  driftBonus === 1 ? "text-green-400" :
                  "text-divine-gold"
                }`}>
                  {matchPersonaConnection}
                </span>
              </div>
            </div>
            <h3 className={`font-semibold text-lg mt-3 transition-colors duration-500 ${
              driftBonus >= 2 ? "text-purple-400" :
              driftBonus === 1 ? "text-green-400" :
              "text-divine-gold"
            }`}>
              {LEVEL_NAMES[matchPersonaConnection]}
            </h3>
            
            {/* Boundary Drift Adjustment Display */}
            {driftBonus > 0 && (
              <div className={`mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                driftBonus >= 2 
                  ? "bg-purple-500/20 text-purple-300 animate-[pulse_0.5s_ease-in-out_infinite]" 
                  : "bg-green-500/20 text-green-300 animate-pulse"
              }`}>
                Boundary Drift Active: +{driftBonus} → Adjusted Level {matchPersonaConnection + driftBonus}
              </div>
            )}
            
            <p className="text-white/70 text-sm text-center px-4 mt-1">
              {LEVEL_DETAILS[matchPersonaConnection]?.description}
            </p>
          </div>
        ) : null}
        
        {/* All Levels List */}
        <div ref={levelScrollRef} className="overflow-y-auto max-h-[40vh] pr-2 space-y-2 py-3">
          {LEVEL_DETAILS.slice(1).map((level) => {
            const isCurrent = level.level === displayLevel;
            const isPast = level.level < displayLevel;
            
            // Check if this level is the drift-adjusted level (works for both modes)
            const driftLevel = driftBonus > 0 
              ? displayLevel + driftBonus 
              : 0;
            const isDriftLevel = level.level === driftLevel && driftLevel !== displayLevel;
            
            return (
              <div 
                key={level.level}
                data-level={level.level}
                className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                  isDriftLevel
                    ? driftBonus >= 2
                      ? "bg-purple-500/20 border border-purple-500/50 animate-[pulse_0.5s_ease-in-out_infinite]"
                      : "bg-green-500/20 border border-green-500/50 animate-pulse"
                    : isCurrent 
                      ? isEscalationMode 
                        ? "bg-emerald-500/20 border border-emerald-500/40"
                        : "bg-divine-gold/20 border border-divine-gold/40" 
                      : isPast 
                        ? "bg-white/5 opacity-60" 
                        : "bg-white/5"
                }`}
              >
                {/* Level number badge */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDriftLevel
                    ? driftBonus >= 2
                      ? "bg-purple-500 text-white font-bold"
                      : "bg-green-500 text-white font-bold"
                    : isCurrent 
                      ? isEscalationMode
                        ? "bg-emerald-500 text-black font-bold"
                        : "bg-divine-gold text-black font-bold" 
                      : isPast 
                        ? "bg-green-500/30 text-green-400"
                        : "bg-white/10 text-white/50"
                }`}>
                  {isPast ? "✓" : level.level}
                </div>
                {/* Level info */}
                <div className="flex-1 min-w-0">
                  <h4 className={`font-medium ${
                    isDriftLevel
                      ? driftBonus >= 2 ? "text-purple-400" : "text-green-400"
                      : isCurrent 
                        ? isEscalationMode ? "text-emerald-400" : "text-divine-gold" 
                        : "text-white/80"
                  }`}>
                    {level.name}
                    {isDriftLevel && <span className="ml-2 text-xs">(Boundary Drift)</span>}
                  </h4>
                  <p className="text-white/50 text-xs mt-0.5">
                    {level.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { LEVEL_DETAILS, LEVEL_NAMES };
export default ConnectionLevelModal;
